import { httpClient } from "./client";
import { InvalidApiResponseError } from "./errors";
import type { QueueApi } from "./interfaces";
import type {
  QueueJoinRequest,
  QueueEntryResponse,
  CourtAssignmentResponse,
  UUID,
} from "../types";
import type {
  QueueStatsResponse,
  PartyInviteResponse,
  PartyMembershipRecoveryResponse,
  PartyResponse,
  CheckedInPlayerResponse,
} from "../types/generated";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isString(value: unknown): value is string {
  return typeof value === "string";
}

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ISO_DATE_TIME_PATTERN =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?$/;

const ASSIGNMENT_TYPES = new Set(["fresh", "top_off"]);
const QUEUE_ENTRY_STATUSES = new Set([
  "waiting",
  "assigned",
  "completed",
  "cancelled",
  "expired",
]);
const WAITING_REASONS = new Set([
  "awaiting_compatible_players",
  "awaiting_target_occupancy",
  "awaiting_court_availability",
  "assignment_pending",
]);
const POLICY_REASONS = new Set([
  "fresh_immediate_ready",
  "fresh_target_reached",
  "fresh_max_wait_reached",
  "fresh_exclusive_party",
  "filled_existing_court",
  "extended_existing_court",
]);
const PARTY_ASSIGNMENT_MODES = new Set(["auto", "manual", "self_serve"]);
const PARTY_STATUSES = new Set(["open", "queued", "playing", "disbanded"]);
const PARTY_ELIGIBILITY_STATES = new Set([
  "ready",
  "blocked",
  "waiting",
  "on_court",
  "unavailable",
]);
const PARTY_ELIGIBILITY_REASONS = new Set([
  "eligible",
  "party_not_open",
  "queue_closed",
  "member_needs_action",
  "party_too_large",
  "facility_closing",
  "no_enabled_court",
  "eligibility_unavailable",
]);
const PARTY_PLACEMENT_ACTIONS = new Set([
  "choose_court",
  "join_auto_queue",
  "join_staff_queue",
  "view_queue",
  "view_court",
  "none",
]);
const PARTY_MEMBER_ADMISSION_STATES = new Set([
  "active",
  "pending_validation",
  "inactive",
  "none",
  "unavailable",
]);
const PARTY_MEMBER_ACTIVITY_STATES = new Set(["none", "waiting", "on_court"]);
const PARTY_MEMBER_ELIGIBILITY_STATES = new Set([
  "eligible",
  "needs_action",
  "waiting",
  "on_court",
  "unavailable",
]);
const PARTY_MEMBER_ELIGIBILITY_REASONS = new Set([
  "eligible",
  "check_in_required",
  "staff_validation_required",
  "session_expired",
  "session_too_short",
  "already_waiting",
  "already_on_court",
  "eligibility_unavailable",
]);
const PARTY_MEMBER_REMEDIATIONS = new Set([
  "none",
  "check_in",
  "present_pass_to_staff",
  "renew_session",
  "view_current_activity",
  "retry_status",
]);

function isUuid(value: unknown): value is string {
  return typeof value === "string" && UUID_PATTERN.test(value);
}

function isIsoDateTime(value: unknown): value is string {
  return (
    typeof value === "string" &&
    ISO_DATE_TIME_PATTERN.test(value) &&
    Number.isFinite(Date.parse(value))
  );
}

function isNullableString(value: unknown): value is string | null | undefined {
  return value == null || typeof value === "string";
}

function isNullableUuid(value: unknown): value is string | null | undefined {
  return value == null || isUuid(value);
}

function isNullableIsoDateTime(
  value: unknown,
): value is string | null | undefined {
  return value == null || isIsoDateTime(value);
}

function isNullableEnum(
  value: unknown,
  allowedValues: ReadonlySet<string>,
): value is string | null | undefined {
  return (
    value == null || (typeof value === "string" && allowedValues.has(value))
  );
}

function isEnum(
  value: unknown,
  allowedValues: ReadonlySet<string>,
): value is string {
  return typeof value === "string" && allowedValues.has(value);
}

function isNullableBoolean(
  value: unknown,
): value is boolean | null | undefined {
  return value == null || typeof value === "boolean";
}

function isInteger(value: unknown): value is number {
  return Number.isInteger(value);
}

function isNonNegativeInteger(value: unknown): value is number {
  return isInteger(value) && (value as number) >= 0;
}

function isNullableInteger(value: unknown): value is number | null | undefined {
  return value == null || isInteger(value);
}

function isQueueEntryResponse(
  value: unknown,
  expectedFacilityId?: UUID,
  expectedEntryId?: UUID,
): value is QueueEntryResponse {
  if (!isRecord(value)) return false;

  const nullableStrings = [
    "avatar_url",
    "court_name",
    "display_name",
    "lead_user_id",
    "rating",
  ] as const;
  const nullableUuids = [
    "assignment_id",
    "court_id",
    "target_court_id",
  ] as const;
  const nullableInstants = [
    "assigned_at",
    "confirm_by",
    "expires_at",
    "last_skipped_at",
    "waiting_until",
  ] as const;

  return (
    isUuid(value.entry_id) &&
    (expectedEntryId === undefined || value.entry_id === expectedEntryId) &&
    isUuid(value.facility_id) &&
    (expectedFacilityId === undefined ||
      value.facility_id === expectedFacilityId) &&
    isUuid(value.group_id) &&
    isString(value.mode) &&
    isEnum(value.status, QUEUE_ENTRY_STATUSES) &&
    isIsoDateTime(value.joined_at) &&
    isInteger(value.skip_count) &&
    typeof value.exclusive === "boolean" &&
    isNullableBoolean(value.checked_in) &&
    isNullableInteger(value.estimated_wait_minutes) &&
    isNullableInteger(value.group_size) &&
    isNullableInteger(value.position) &&
    nullableStrings.every((field) => isNullableString(value[field])) &&
    nullableUuids.every((field) => isNullableUuid(value[field])) &&
    nullableInstants.every((field) => isNullableIsoDateTime(value[field])) &&
    isNullableEnum(value.assignment_type, ASSIGNMENT_TYPES) &&
    isNullableEnum(value.predicted_assignment_type, ASSIGNMENT_TYPES) &&
    isNullableEnum(value.waiting_reason, WAITING_REASONS) &&
    isNullableEnum(value.policy_reason, POLICY_REASONS)
  );
}

function isPartyMemberEligibility(value: unknown): boolean {
  if (
    !(
      isRecord(value) &&
      isUuid(value.member_id) &&
      typeof value.is_current_user === "boolean" &&
      typeof value.is_leader === "boolean" &&
      !("user_id" in value) &&
      isNullableString(value.display_name) &&
      isNullableString(value.avatar_url) &&
      isEnum(value.admission_state, PARTY_MEMBER_ADMISSION_STATES) &&
      isEnum(value.activity_state, PARTY_MEMBER_ACTIVITY_STATES) &&
      isEnum(value.state, PARTY_MEMBER_ELIGIBILITY_STATES) &&
      isEnum(value.reason, PARTY_MEMBER_ELIGIBILITY_REASONS) &&
      isEnum(value.remediation, PARTY_MEMBER_REMEDIATIONS) &&
      isNullableString(value.action_url) &&
      isNullableIsoDateTime(value.session_expires_at)
    )
  ) {
    return false;
  }

  if (value.state === "eligible") {
    return (
      value.admission_state === "active" &&
      value.activity_state === "none" &&
      value.reason === "eligible" &&
      value.remediation === "none"
    );
  }
  if (value.state === "waiting") {
    return (
      value.activity_state === "waiting" &&
      value.reason === "already_waiting" &&
      value.remediation === "view_current_activity"
    );
  }
  if (value.state === "on_court") {
    return (
      value.activity_state === "on_court" &&
      value.reason === "already_on_court" &&
      value.remediation === "view_current_activity"
    );
  }
  return (
    value.activity_state === "none" &&
    value.reason !== "eligible" &&
    value.reason !== "already_waiting" &&
    value.reason !== "already_on_court" &&
    value.remediation !== "none"
  );
}

export function isPartyEligibilityResponse(
  value: unknown,
  expectedFacilityId: UUID,
  expectedPartyId: UUID,
): boolean {
  if (
    !isRecord(value) ||
    !isUuid(value.party_id) ||
    value.party_id !== expectedPartyId ||
    !isUuid(value.facility_id) ||
    value.facility_id !== expectedFacilityId ||
    !isNullableString(value.facility_name) ||
    !isNullableEnum(value.assignment_mode, PARTY_ASSIGNMENT_MODES) ||
    !isEnum(value.state, PARTY_ELIGIBILITY_STATES) ||
    !isEnum(value.reason, PARTY_ELIGIBILITY_REASONS) ||
    typeof value.leader_can_place !== "boolean" ||
    !isEnum(value.action, PARTY_PLACEMENT_ACTIONS) ||
    !isNonNegativeInteger(value.revision) ||
    !isNullableIsoDateTime(value.minimum_play_end) ||
    !Array.isArray(value.members) ||
    !value.members.every(isPartyMemberEligibility)
  ) {
    return false;
  }

  const memberIds = value.members.map((member) =>
    isRecord(member) ? member.member_id : undefined,
  );
  if (new Set(memberIds).size !== memberIds.length) return false;

  if (value.state === "ready") {
    const actionForMode = {
      auto: "join_auto_queue",
      manual: "join_staff_queue",
      self_serve: "choose_court",
    } as const;
    return (
      value.reason === "eligible" &&
      value.leader_can_place &&
      typeof value.assignment_mode === "string" &&
      value.action ===
        actionForMode[value.assignment_mode as keyof typeof actionForMode] &&
      value.members.every(
        (member) => isRecord(member) && member.state === "eligible",
      )
    );
  }
  if (value.state === "waiting") {
    return (
      value.reason === "party_not_open" &&
      !value.leader_can_place &&
      value.action === "view_queue" &&
      value.members.every(
        (member) => isRecord(member) && member.state === "waiting",
      )
    );
  }
  if (value.state === "on_court") {
    return (
      value.reason === "party_not_open" &&
      !value.leader_can_place &&
      value.action === "view_court"
    );
  }
  if (value.state === "unavailable") {
    return (
      value.reason === "eligibility_unavailable" &&
      !value.leader_can_place &&
      value.action === "none"
    );
  }
  return (
    value.reason !== "eligible" &&
    value.reason !== "eligibility_unavailable" &&
    !value.leader_can_place &&
    value.action === "none"
  );
}

function isPartyResponse(
  value: unknown,
  expectedFacilityId?: UUID,
  expectedPartyId?: UUID,
): value is PartyResponse {
  if (
    !isRecord(value) ||
    !isRecord(value.capacity) ||
    !isUuid(value.party_id)
  ) {
    return false;
  }

  return (
    isUuid(value.facility_id) &&
    (expectedFacilityId === undefined ||
      value.facility_id === expectedFacilityId) &&
    (expectedPartyId === undefined || value.party_id === expectedPartyId) &&
    !("leader_id" in value) &&
    isRecord(value.viewer) &&
    (value.viewer.role === "leader" ||
      value.viewer.role === "member" ||
      value.viewer.role === "operator") &&
    typeof value.viewer.can_invite === "boolean" &&
    typeof value.viewer.can_leave === "boolean" &&
    typeof value.viewer.can_remove_members === "boolean" &&
    typeof value.viewer.can_place === "boolean" &&
    ((value.viewer.role === "leader" &&
      value.viewer.can_invite &&
      value.viewer.can_leave &&
      value.viewer.can_remove_members &&
      value.viewer.can_place) ||
      (value.viewer.role === "member" &&
        value.viewer.can_invite &&
        value.viewer.can_leave &&
        !value.viewer.can_remove_members &&
        !value.viewer.can_place) ||
      (value.viewer.role === "operator" &&
        !value.viewer.can_invite &&
        !value.viewer.can_leave &&
        !value.viewer.can_remove_members &&
        !value.viewer.can_place)) &&
    isString(value.code) &&
    isEnum(value.status, PARTY_STATUSES) &&
    isIsoDateTime(value.created_at) &&
    typeof value.leader_exit_disbands === "boolean" &&
    isNullableUuid(value.entry_id) &&
    isPartyEligibilityResponse(
      value.eligibility,
      value.facility_id,
      value.party_id,
    ) &&
    (() => {
      if (!isRecord(value.eligibility)) return false;
      if (value.status === "open") {
        return (
          value.entry_id == null &&
          value.eligibility.state !== "waiting" &&
          value.eligibility.state !== "on_court" &&
          value.eligibility.reason !== "party_not_open"
        );
      }
      if (value.status === "queued") {
        return (
          isUuid(value.entry_id) &&
          value.eligibility.state === "waiting" &&
          value.eligibility.reason === "party_not_open" &&
          value.eligibility.action === "view_queue"
        );
      }
      if (value.status === "playing") {
        return (
          isUuid(value.entry_id) &&
          value.eligibility.state === "on_court" &&
          value.eligibility.reason === "party_not_open" &&
          value.eligibility.action === "view_court"
        );
      }
      return (
        value.entry_id == null &&
        value.eligibility.state === "blocked" &&
        value.eligibility.reason === "party_not_open" &&
        value.eligibility.action === "none"
      );
    })() &&
    (() => {
      if (
        !isRecord(value.eligibility) ||
        !Array.isArray(value.eligibility.members)
      ) {
        return false;
      }
      const currentUsers = value.eligibility.members.filter(
        (member) => isRecord(member) && member.is_current_user === true,
      );
      const leaders = value.eligibility.members.filter(
        (member) => isRecord(member) && member.is_leader === true,
      );
      if (value.status === "disbanded") {
        return currentUsers.length <= 1 && leaders.length <= 1;
      }
      if (leaders.length !== 1) return false;
      if (value.viewer.role === "operator") return currentUsers.length === 0;
      if (currentUsers.length !== 1) return false;
      return value.viewer.role === "leader"
        ? currentUsers[0] === leaders[0]
        : currentUsers[0] !== leaders[0];
    })() &&
    isNonNegativeInteger(value.capacity.accepted) &&
    isNonNegativeInteger(value.capacity.pending) &&
    isNonNegativeInteger(value.capacity.available) &&
    isInteger(value.capacity.maximum) &&
    (value.capacity.maximum as number) >= 1 &&
    (value.capacity.maximum as number) <= 6 &&
    (() => {
      if (
        !isRecord(value.eligibility) ||
        !Array.isArray(value.eligibility.members) ||
        !isNonNegativeInteger(value.capacity.accepted) ||
        !isNonNegativeInteger(value.capacity.pending) ||
        !isNonNegativeInteger(value.capacity.available) ||
        !isInteger(value.capacity.maximum)
      ) {
        return false;
      }
      return (
        value.capacity.accepted === value.eligibility.members.length &&
        value.capacity.accepted +
          value.capacity.pending +
          value.capacity.available ===
          value.capacity.maximum
      );
    })()
  );
}

async function getValidated<T>(
  endpoint: string,
  isValid: (value: unknown) => value is T,
): Promise<T> {
  const response = await httpClient.get<unknown>(endpoint);
  if (!isValid(response)) throw new InvalidApiResponseError(endpoint);
  return response;
}

async function getNullableValidated<T>(
  endpoint: string,
  isValid: (value: unknown) => value is T,
): Promise<T | null> {
  const response = await httpClient.get<unknown>(endpoint);
  if (response === null) return null;
  if (!isValid(response)) throw new InvalidApiResponseError(endpoint);
  return response;
}

export const queueApi: QueueApi = {
  join: (request: QueueJoinRequest) =>
    httpClient.post<QueueEntryResponse>("/queue", request),

  /** Live facility-wide queue signal: queue_size, avg_wait_minutes, courts
   * available/total. Used by the drop-in discovery overlay (active windows
   * only). NOT "spots left" — drop-in is queue-based, not capacity-based. */
  getStats: (facilityId: UUID) =>
    httpClient.get<QueueStatsResponse>(
      `/queue/stats?facility_id=${encodeURIComponent(facilityId)}`,
    ),

  leave: (entryId: UUID) => httpClient.delete<void>(`/queue/${entryId}`),

  getStatus: (entryId: UUID) => {
    const endpoint = `/queue/${entryId}`;
    return getValidated(endpoint, (value): value is QueueEntryResponse =>
      isQueueEntryResponse(value, undefined, entryId),
    );
  },

  /** The caller's ACTIVE queue entry at a facility (solo or party member) —
   * recovery for a lost entry_id (Phase C). Normal absence is 200 + null.
   * Declared server-side BEFORE /queue/{entry_id} so "mine" isn't parsed as a UUID. */
  getMyEntry: (facilityId: UUID) => {
    const endpoint = `/queue/mine?facility_id=${encodeURIComponent(facilityId)}`;
    return getNullableValidated(
      endpoint,
      (value): value is QueueEntryResponse =>
        isQueueEntryResponse(value, facilityId),
    );
  },

  /** The calling player's current court assignment for their own entry,
   * including the required Facility-owned court_name plus
   * assignment_type/policy_reason metadata for allowlisted UI copy.
   * 404 when the entry does not exist or has no assignment yet. */
  getAssignment: (entryId: UUID) =>
    httpClient.get<CourtAssignmentResponse>(`/queue/${entryId}/assignment`),

  assignCourt: (entryId: UUID, courtId: UUID, sessionDurationMinutes: number) =>
    httpClient.post<CourtAssignmentResponse>(`/queue/${entryId}/assign`, {
      court_id: courtId,
      session_duration_minutes: sessionDurationMinutes,
    }),

  // (frictionless drop-in 2026-07-02: confirmCheckin removed — a drop-in
  // assignment is on court immediately; POST /queue/assignments/{id}/checkin is gone.)

  // Party (generated PartyResponse — carries entry_id when queued/playing, Phase C)
  createParty: (facilityId: UUID) =>
    httpClient.post<PartyResponse>("/queue/party", { facility_id: facilityId }),

  joinParty: (code: string) =>
    httpClient.post<PartyResponse>("/queue/party/join", { code }),

  leaveParty: (partyId: UUID) =>
    httpClient.delete<void>(`/queue/party/${partyId}`),

  getParty: (partyId: UUID) => {
    const endpoint = `/queue/party/${partyId}`;
    return getValidated(endpoint, (value): value is PartyResponse =>
      isPartyResponse(value, undefined, partyId),
    );
  },

  /** The caller's current NON-terminal party at a facility (open/queued/
   * playing, leader or member) — recovery for a player stranded after their
   * game ends (party-return-after-game fix). Normal absence is 200 + null.
   * Declared server-side BEFORE /queue/party/{party_id} so "mine" isn't parsed
   * as a UUID. */
  getMyParty: (facilityId: UUID) => {
    const endpoint = `/queue/party/mine?facility_id=${encodeURIComponent(facilityId)}`;
    return getNullableValidated(endpoint, (value): value is PartyResponse =>
      isPartyResponse(value, facilityId),
    );
  },

  listPartyMemberships: (facilityId: UUID, userId?: string) => {
    const params = new URLSearchParams({ facility_id: facilityId });
    if (userId) params.set("user_id", userId);
    return httpClient.get<PartyMembershipRecoveryResponse[]>(
      `/queue/party/memberships?${params.toString()}`,
    );
  },

  removePartyMembership: (partyId: UUID, targetUserId: string) =>
    httpClient.delete<void>(
      `/queue/party/${encodeURIComponent(partyId)}/members/${encodeURIComponent(targetUserId)}`,
    ),

  // targetCourtId (ADR-025 Phase D party-lines, #64): queue the party into a
  // specific court's self-serve line instead of the facility queue. Omitted
  // from the body entirely when undefined (matches the backend's None
  // default — never sends target_court_id: undefined).
  queueParty: (partyId: UUID, mode: string, targetCourtId?: UUID) =>
    httpClient.post<QueueEntryResponse>(`/queue/party/${partyId}/queue`, {
      mode,
      ...(targetCourtId ? { target_court_id: targetCourtId } : {}),
    }),

  // Leader removes a member by the eligibility roster's opaque member UUID.
  // The acting leader is the token user — never a body field.
  kickPartyMember: (partyId: UUID, targetMemberId: UUID) =>
    httpClient.post<void>(`/queue/party/${partyId}/kick`, {
      target_member_id: targetMemberId,
    }),

  // Party invites. The inviter/responder is the token user — never a body field.
  inviteToParty: (partyId: UUID, inviteeUserId: string) =>
    httpClient.post<PartyInviteResponse>(`/queue/party/${partyId}/invites`, {
      invitee_user_id: inviteeUserId,
    }),

  listMyPartyInvites: () =>
    httpClient.get<PartyInviteResponse[]>(`/queue/party-invites/mine`),

  // A party's outgoing pending invites. The endpoint remains member-gated, while
  // the player UI loads this management projection only for the party leader.
  listPartyInvites: (partyId: UUID) =>
    httpClient.get<PartyInviteResponse[]>(`/queue/party/${partyId}/invites`),

  respondPartyInvite: (inviteId: UUID, accept: boolean) =>
    httpClient.post<PartyInviteResponse>(
      `/queue/party-invites/${inviteId}/respond`,
      { accept },
    ),

  cancelPartyInvite: (inviteId: UUID) =>
    httpClient.delete<void>(`/queue/party-invites/${inviteId}`),

  // "Recent + search" invite-candidates picker (party-persistence + invite-picker
  // rework, Task 6). `q` is omitted from the querystring when blank — that's the
  // "recently active" default list; a non-blank `q` switches to a server-side
  // name search. Both are the SAME endpoint/response shape.
  getInviteCandidates: (
    partyId: UUID,
    options?: { q?: string; limit?: number },
  ) => {
    const params = new URLSearchParams();
    const q = options?.q?.trim();
    if (q) params.set("q", q);
    if (options?.limit !== undefined)
      params.set("limit", String(options.limit));
    const qs = params.toString();
    return httpClient.get<CheckedInPlayerResponse[]>(
      `/queue/party/${partyId}/invite-candidates${qs ? `?${qs}` : ""}`,
    );
  },
};
